import { LayoutSchema, SuperSchema } from '@updater/ui-informant';
import { QuestionForm } from 'types';
import { merchandisingLayoutSchema } from './layoutSchema';
import { merchandisingSchema } from './schema';

/**
 * Applied after any screen filtering, so it lands on the question the visitor
 * actually opens on rather than the one declared first.
 */
function hideFirstBackButton(
  screens: LayoutSchema['screens'],
  hide?: boolean
): LayoutSchema['screens'] {
  if (!hide) return screens;
  return screens.map((screen, index) =>
    index === 0 ? { ...screen, hideBackButton: true } : screen
  );
}

export interface BuildQuizLayoutOptions {
  /** Hides the back button on the first question only, when the app runs in an iframe */
  hideFirstPageBackButton?: boolean;
}

export interface BuildQuizLayoutResult {
  schema: SuperSchema;
  layoutSchema: LayoutSchema;
  initialValues: Partial<QuestionForm>;
  /**
   * Where informant enters the flow when the URL names no screen.
   *
   * 'latest' enters on the first question that is not yet answered validly,
   * which is what lets a visitor resume where they left off. It relies on the
   * first question being unanswered — and any question carrying a schema
   * `default` is answered before it is ever shown. The quiz opens on
   * `internetUsers`, which carries no default for exactly this reason, so
   * nothing is skipped.
   */
  restartPolicy: 'latest';
}

/**
 * Derives the quiz — its questions, screens and seeded answers — from a
 * microsite's configuration.
 *
 * Returns fresh objects every call and never mutates the exported
 * `merchandisingLayoutSchema` — it is module-level state shared by every
 * microsite, so editing it in place would leak one tenant's configuration into
 * the next.
 */
export function buildQuizLayout({
  hideFirstPageBackButton,
}: BuildQuizLayoutOptions): BuildQuizLayoutResult {
  return {
    schema: merchandisingSchema,
    layoutSchema: {
      ...merchandisingLayoutSchema,
      screens: hideFirstBackButton(
        [...merchandisingLayoutSchema.screens],
        hideFirstPageBackButton
      ),
    },
    initialValues: {},
    restartPolicy: 'latest',
  };
}
